import { useMemo } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  redirect,
} from 'react-router-dom';

import { AppRoot, AppRootErrorBoundary } from '@/app/components/root';
import { paths } from '@/app/router/paths';
import { UpdatePasswordPage } from '@/features/auth/components/pages/update-password-page';
import { ProtectedRoute } from '@/features/auth/components/protected-route';
import { PublicRoute } from '@/features/auth/components/public-route';
import OverviewPage from '@/features/dashboard/components/pages/overview-page';
import DocumentsHubPage from '@/features/documents/components/pages/documents-hub-page';
import { ForecastsHubPage } from '@/features/forecasts/components/pages/forecasts-hub-page';
import { InsightsHubPage } from '@/features/insights/components/pages/insights-hub-page';
import { NotificationHubPage } from '@/features/notifications/components/pages/notification-hub-page';
import { ManageOrgInvitation } from '@/features/org/components/manage-org-invitation';
import { ManagePartnerInvitation } from '@/features/partners/components/manage-partner-invitation';
import { AcceptSubprojectInvitationPage } from '@/features/partners/components/pages/accept-subproject-invitation-page';
import { PartnerHubPage } from '@/features/partners/components/pages/partner-hub-page';
import { CreateProjectPage } from '@/features/projects/create-project/components/pages/create-project-page';
import { TrackingReportHistoryHub } from '@/features/projects/kpi-tracking-entry/components/tracking-report-history-hub';
import { ClaimProjectOwnershipPage } from '@/features/projects/project-details/components/claim-project-ownership-page';
import { ProjectDetailsPage } from '@/features/projects/project-details/components/pages/project-details-page/project-details-page';
import { ManageProjectInvitation } from '@/features/projects/project-invitation/components/manage-project-invitation';
import { ProjectsHubPage } from '@/features/projects/projects-hub/components/pages/projects-hub-page';
import { ProjectMemberManagementPage } from '@/features/projects/shared/components/project-member-management/project-member-management-page';
import { SubprojectHubPage } from '@/features/projects/subprojects-hub/components/pages/subproject-hub-page';
import { CheckoutSuccessPage } from '@/features/subscriptions/components/pages/checkout-success-page';
import { PlansManagementPage } from '@/features/subscriptions/components/pages/plans-management-page';

export const createAppRouter = () =>
  createBrowserRouter([
    {
      path: paths.home.path,
      element: (
        <ProtectedRoute>
          <AppRoot />
        </ProtectedRoute>
      ),
      children: [
        { index: true, loader: () => redirect(paths.dashboard.path) },
        { path: paths.dashboard.path, element: <OverviewPage /> },
        { path: paths.projects.hub.path, element: <ProjectsHubPage /> },
        { path: paths.insights.path, element: <InsightsHubPage /> },
        { path: paths.forecast.path, element: <ForecastsHubPage /> },
        {
          path: paths.plansManagement.path,
          element: <PlansManagementPage />,
        },
        {
          path: paths.checkoutSuccess.path,
          element: <CheckoutSuccessPage />,
        },
        {
          path: paths.documents.path,
          element: <DocumentsHubPage />,
        },
        {
          path: paths.partners.hub.path,
          element: <PartnerHubPage />,
        },
        {
          path: paths.notifications.path,
          element: <NotificationHubPage />,
        },
        {
          path: paths.settings.path,
          lazy: async () => {
            const { SettingsPage } =
              await import('@/features/profile/components/pages/settings-page');
            return { Component: SettingsPage };
          },
        },
        {
          path: paths.projects.details.base.path,
          element: <ProjectDetailsPage />,
        },
        {
          path: paths.projects.subproject.hub.path,
          element: <SubprojectHubPage />,
        },
        {
          path: paths.projects.details.trackingReportHistory.path,
          element: <TrackingReportHistoryHub />,
        },

        {
          path: paths.projects.details.memberManagement.path,
          element: <ProjectMemberManagementPage />,
        },
        {
          path: paths.projects.details.contractorManagement.path,
          lazy: async () => {
            const { ContractorManagementPage } =
              await import('@/features/projects/shared/components/contractor-management/contractor-management-page');
            return { Component: ContractorManagementPage };
          },
        },
      ],
    },
    {
      path: paths.auth.register.path,
      lazy: async () => {
        const { RegisterPage } =
          await import('@/features/auth/components/pages/register-page');
        return {
          Component: () => (
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          ),
        };
      },
    },
    {
      path: paths.auth.login.path,
      lazy: async () => {
        const { LoginPage } =
          await import('@/features/auth/components/pages/login-page');
        return {
          Component: () => (
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          ),
        };
      },
    },
    {
      path: paths.auth.resetPassword.path,
      lazy: async () => {
        const { ResetPasswordPage } =
          await import('@/features/auth/components/pages/reset-password-page');
        return {
          Component: () => (
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          ),
        };
      },
    },
    {
      path: paths.auth.updatePassword.path,
      element:
        import.meta.env.MODE === 'development' ? (
          <UpdatePasswordPage />
        ) : (
          <ProtectedRoute>
            <UpdatePasswordPage />
          </ProtectedRoute>
        ),
    },

    {
      path: paths.org.create.path,
      lazy: async () => {
        const { CreateOrgPage } =
          await import('@/features/org/components/pages/create-org-page');
        return {
          Component: () => (
            <ProtectedRoute>
              <CreateOrgPage />
            </ProtectedRoute>
          ),
        };
      },
    },
    {
      path: paths.org.manageInvitation.path,
      element: (
        <ProtectedRoute>
          <ManageOrgInvitation />
        </ProtectedRoute>
      ),
    },
    {
      path: paths.org.memberManagement.path,
      lazy: async () => {
        const { OrgMemberManagementPage } =
          await import('@/features/org/components/pages/org-member-management/org-member-management-page');
        return {
          Component: () => (
            <ProtectedRoute>
              <OrgMemberManagementPage />
            </ProtectedRoute>
          ),
        };
      },
    },
    {
      path: paths.projects.manageMemberInvitation.path,
      element: (
        <ProtectedRoute>
          <ManageProjectInvitation />
        </ProtectedRoute>
      ),
    },
    {
      path: paths.projects.claimOwnership.path,
      element: <ClaimProjectOwnershipPage />,
    },
    {
      path: paths.partners.manageInvitation.path,
      element: (
        <ProtectedRoute>
          <ManagePartnerInvitation />
        </ProtectedRoute>
      ),
    },
    {
      path: paths.partners.acceptInvitation.path,
      element: (
        <ProtectedRoute>
          <AcceptSubprojectInvitationPage />
        </ProtectedRoute>
      ),
    },
    {
      path: paths.projects.create.base.path,
      element: (
        <ProtectedRoute>
          <CreateProjectPage />
        </ProtectedRoute>
      ),
    },

    {
      path: paths.projects.subproject.create.path,
      lazy: async () => {
        const { CreateSubprojectPage } =
          await import('@/features/projects/create-subproject/components/pages/create-subproject-page');
        return {
          Component: () => (
            <ProtectedRoute>
              <CreateSubprojectPage />
            </ProtectedRoute>
          ),
        };
      },
    },
    {
      path: paths.partners.acceptContractorSubproject.path,
      element: (
        <ProtectedRoute>
          <AcceptSubprojectInvitationPage />
        </ProtectedRoute>
      ),
    },
    {
      path: '*',
      lazy: async () => {
        const { NotFoundRoute } = await import('@/app/components/not-found');
        return { Component: NotFoundRoute };
      },
      ErrorBoundary: AppRootErrorBoundary,
    },
  ]);

export const AppRouter = () => {
  const router = useMemo(() => createAppRouter(), []);

  return <RouterProvider router={router} />;
};
